/**
 * Экран не гаснет, пока есть хоть один держатель (Screen Wake Lock API).
 *
 * Блокировка одна на окно, держателей сколько угодно: звонок этого окна и
 * звонок свёрнутого окна звонка (`startCallWindowScreenAwake`) друг другу не
 * мешают. Скрытой вкладке браузер блокировку не даёт и снимает её сам — на
 * возврате видимости берём заново. Нет API или отказ (сборка десктопа без
 * разрешения `screen-wake-lock`, вкладка скрылась посреди запроса) — молча:
 * экран гаснет по таймеру, как и без блокировки.
 */

let holders = 0;
let sentinel: WakeLockSentinel | null = null;
let requesting = false;

const readWakeLock = (): WakeLock | null =>
  typeof navigator !== "undefined" && "wakeLock" in navigator
    ? navigator.wakeLock
    : null;

const acquire = (): void => {
  if (holders === 0 || sentinel !== null || requesting) return;
  if (document.visibilityState !== "visible") return;
  const wakeLock = readWakeLock();
  if (!wakeLock) return;
  requesting = true;
  wakeLock.request("screen").then(
    (lock) => {
      requesting = false;
      // Держатели ушли, пока ждали ответа, — блокировка уже не нужна.
      if (holders === 0) {
        void lock.release().catch(() => undefined);
        return;
      }
      sentinel = lock;
      lock.addEventListener("release", () => {
        if (sentinel === lock) sentinel = null;
      });
    },
    () => {
      requesting = false;
    },
  );
};

const onVisibilityChange = (): void => acquire();

/** Держать экран включённым; возвращает снятие (повторный вызов — no-op). */
export const holdScreenAwake = (): (() => void) => {
  holders += 1;
  if (holders === 1) {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }
  acquire();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
    if (holders > 0) return;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    const lock = sentinel;
    sentinel = null;
    void lock?.release().catch(() => undefined);
  };
};

export const __resetScreenAwakeForTests = (): void => {
  document.removeEventListener("visibilitychange", onVisibilityChange);
  holders = 0;
  sentinel = null;
  requesting = false;
};
